import { useRef, useState } from 'react';

interface GalleryUploaderProps {
  value: string[];
  onChange: (urls: string[]) => void;
  label?: string;
  maxImages?: number;
}

export default function GalleryUploader({
  value = [],
  onChange,
  label = '活动相册',
  maxImages = 20,
}: GalleryUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // 上传图片到服务器
  const uploadToServer = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '上传失败');
      }

      const data = await response.json();
      return data.data.url;
    } catch (err) {
      console.error('Upload failed:', err);
      throw err;
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    // 检查是否超过最大数量
    if (value.length + files.length > maxImages) {
      setError(`最多只能上传 ${maxImages} 张图片`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const newUrls: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (!file.type.startsWith('image/')) {
          setError('请选择图片文件');
          continue;
        }
        
        if (file.size > 10 * 1024 * 1024) {
          setError('图片文件过大，请选择小于 10MB 的文件');
          continue;
        }

        const url = await uploadToServer(file);
        if (url) {
          newUrls.push(url);
        }
      }

      if (newUrls.length > 0) {
        onChange([...value, ...newUrls]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败，请重试');
    } finally {
      setLoading(false);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleRemove = (index: number) => {
    const newUrls = value.filter((_, i) => i !== index);
    onChange(newUrls);
    setError('');
  };

  const handleUrlInput = () => {
    const url = prompt('请输入图片 URL：');
    if (url && url.trim()) {
      if (value.length >= maxImages) {
        setError(`最多只能上传 ${maxImages} 张图片`);
        return;
      }
      if (url.trim().startsWith('data:image')) {
        setError('不支持 base64 格式，请使用图片上传功能或输入图片 URL');
        return;
      }
      onChange([...value, url.trim()]);
      setError('');
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newUrls = [...value];
    [newUrls[index - 1], newUrls[index]] = [newUrls[index], newUrls[index - 1]];
    onChange(newUrls);
  };

  const handleMoveDown = (index: number) => {
    if (index === value.length - 1) return;
    const newUrls = [...value];
    [newUrls[index], newUrls[index + 1]] = [newUrls[index + 1], newUrls[index]];
    onChange(newUrls);
  };

  // Drag and drop reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newUrls = [...value];
    const draggedItem = newUrls[draggedIndex];
    newUrls.splice(draggedIndex, 1);
    newUrls.splice(index, 0, draggedItem);
    onChange(newUrls);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-xs font-medium text-[#374151]">{label}</label>
        <span className="text-[10px] text-[#9CA3AF]">
          {value.length} / {maxImages} 张
        </span>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
          {error}
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="mb-3 px-4 py-6 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full mx-auto mb-2"></div>
          <div className="text-sm text-[#6B7280]">上传中...</div>
        </div>
      )}

      {/* Image grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-3">
          {value.map((url, index) => (
            <div
              key={`${url}-${index}`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`relative group rounded-xl overflow-hidden border-2 transition-all ${
                draggedIndex === index
                  ? 'border-[#3B82F6] opacity-50'
                  : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
              }`}
            >
              <div className="aspect-square">
                <img
                  src={url}
                  alt={`图片 ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Overlay with actions */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex flex-col gap-1">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="px-2 py-1 bg-white text-[#374151] text-xs font-medium rounded cursor-pointer hover:bg-[#F3F4F6] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <i className="ri-arrow-up-line" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === value.length - 1}
                      className="px-2 py-1 bg-white text-[#374151] text-xs font-medium rounded cursor-pointer hover:bg-[#F3F4F6] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <i className="ri-arrow-down-line" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="px-2 py-1 bg-red-500 text-white text-xs font-medium rounded cursor-pointer hover:bg-red-600 transition-colors"
                    title="删除"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>

              {/* Image number badge */}
              <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-black/60 text-white text-[10px] font-medium rounded">
                {index + 1}
              </div>

              {/* Drag handle indicator */}
              <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 bg-black/60 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-drag-move-2-line" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload buttons */}
      {!loading && value.length < maxImages && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 py-2.5 px-4 border-2 border-dashed border-[#D1D5DB] rounded-xl text-sm text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] transition-colors flex items-center justify-center gap-2 cursor-pointer"
          >
            <i className="ri-upload-cloud-2-line" />
            上传图片
          </button>
          <button
            type="button"
            onClick={handleUrlInput}
            className="flex-1 py-2.5 px-4 border-2 border-dashed border-[#D1D5DB] rounded-xl text-sm text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] transition-colors flex items-center justify-center gap-2 cursor-pointer"
          >
            <i className="ri-links-line" />
            添加URL
          </button>
        </div>
      )}

      {/* File input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Help text */}
      <p className="text-[10px] text-[#9CA3AF] mt-2">
        支持 JPG、PNG、GIF、WebP、SVG 格式，最大 10MB/张。可拖拽调整顺序。
      </p>
    </div>
  );
}
